/**
 * TRowForm - Form for standard T-hangar row template
 *
 * Supports 2-30 alternating T-shaped units with real-time preview.
 * Even units face south, odd units face north.
 */

import { useForm, type Resolver } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Loader2, ChevronDown, ChevronRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Slider } from '@/components/ui/slider'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Separator } from '@/components/ui/separator'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import { cn } from '@/lib/utils'
import {
  tHangarParamsSchema,
  DEFAULT_T_HANGAR_VALUES,
  type THangarParams,
  calculateStepWidth,
  calculateUnitPitch,
  calculateTShapeArea,
} from '@/lib/templates/standard-t-hangar/params'
import {
  calculateTHangarLayout,
} from '@/lib/templates/standard-t-hangar/calculations'
import type { DoorType, UnitDoorsConfig } from '@/lib/templates/types'
import { createDefaultTHangarDoorSpec } from '@/lib/templates/standard-t-hangar/params'
import { useBuildingEditorStore } from '@/stores/buildingEditorStore'
import { THangarUnitDoorEditor } from './THangarUnitDoorEditor'
import { RemoveAreaButton } from '../shared/RemoveAreaButton'
import { PricingFeaturesSection } from './PricingFeaturesSection'
import { pricingFieldsSchema, defaultPricingValues, type PricingFormFields } from '@/lib/schemas/pricing-fields'

interface TRowFormProps {
  initialValues?: Partial<THangarParams>
  onValuesChange?: (values: THangarParams) => void
  onSave: (values: THangarParams, pricing?: PricingFormFields) => void
  onCancel: () => void
  isCreating?: boolean
  isSaving?: boolean
  previewLatitude?: number
  previewLongitude?: number
  isPlaced?: boolean
  areaId?: string
  areaName?: string
  onRemoved?: () => void
  initialPricing?: Partial<PricingFormFields>
}

export function TRowForm({
  initialValues,
  onValuesChange,
  onSave,
  onCancel,
  isCreating = true,
  isSaving = false,
  previewLatitude,
  previewLongitude,
  isPlaced = true,
  areaId,
  areaName,
  onRemoved,
  initialPricing,
}: TRowFormProps) {
  const isInitialMount = useRef(true)

  const {
    tHangarFormValues,
        updateTHangarFormValues,
      } = useBuildingEditorStore()

  const form = useForm<THangarParams>({
    // zodResolver types its result as Resolver<z.input, any, z.output>; schema
    // `.default()`s make those differ, so it will not unify with the form's
    // output-typed values under react-hook-form v7.67. Defaults are applied at
    // parse time, so the cast is sound.
    resolver: zodResolver(tHangarParamsSchema) as unknown as Resolver<THangarParams>,
    defaultValues: {
      ...DEFAULT_T_HANGAR_VALUES,
      ...initialValues,
      ...(tHangarFormValues || {}),
    },
  })

  const pricingForm = useForm<PricingFormFields>({
    // zodResolver types its result as Resolver<z.input, any, z.output>; schema
    // `.default()`s make those differ, so it will not unify with the form's
    // output-typed values under react-hook-form v7.67. Defaults are applied at
    // parse time, so the cast is sound.
    resolver: zodResolver(pricingFieldsSchema) as unknown as Resolver<PricingFormFields>,
    defaultValues: { ...defaultPricingValues, ...initialPricing },
  })

  const { register, handleSubmit, watch, setValue, formState: { errors } } = form

  const watchedValues = watch()

  // Calculate layout with current values
  const layoutResult = useMemo(() => {
    return calculateTHangarLayout(watchedValues)
  }, [
    watchedValues.unit_width_ft,
    watchedValues.wing_depth_ft,
    watchedValues.tail_width_ft,
    watchedValues.unit_count,
    watchedValues.wall_thickness_ft,
    watchedValues.height_ft,
  ])

  // Derived values
  const stepWidth = useMemo(() => calculateStepWidth(watchedValues), [
    watchedValues.unit_width_ft, watchedValues.tail_width_ft,
  ])
  const unitPitch = useMemo(() => calculateUnitPitch(watchedValues), [
    watchedValues.unit_width_ft, watchedValues.tail_width_ft, watchedValues.wall_thickness_ft,
  ])
  const unitArea = useMemo(() => calculateTShapeArea(watchedValues), [
    watchedValues.unit_width_ft, watchedValues.wing_depth_ft, watchedValues.tail_width_ft,
  ])

  // Sync form values to store for preview updates
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false
      return
    }
    updateTHangarFormValues(watchedValues)
  }, [
    watchedValues.unit_width_ft,
    watchedValues.wing_depth_ft,
    watchedValues.tail_width_ft,
    watchedValues.height_ft,
    watchedValues.unit_count,
    watchedValues.wall_thickness_ft,
    watchedValues.default_door_type,
    watchedValues.default_door_width_ft,
    watchedValues.default_door_height_ft,
    watchedValues.floor_color,
    watchedValues.wall_color,
    watchedValues.bearing_deg,
    watchedValues.name,
    watchedValues.code,
    JSON.stringify(watchedValues.unit_overrides),
    updateTHangarFormValues,
  ])

  // Sync bearing from store to form when changed externally (e.g., rotation ring)
  useEffect(() => {
    const storeBearing = tHangarFormValues?.bearing_deg
    const formBearing = watchedValues.bearing_deg
    if (storeBearing !== undefined && Math.abs(storeBearing - formBearing) > 0.1) {
      setValue('bearing_deg', storeBearing)
    }
  }, [tHangarFormValues?.bearing_deg, setValue])

  // Notify parent of value changes
  useEffect(() => {
    if (onValuesChange) {
      onValuesChange(watchedValues)
    }
  }, [watchedValues, onValuesChange])

  // Handle unit count change
  const handleUnitCountChange = (value: number[]) => {
    setValue('unit_count', value[0])
    setValue('unit_overrides', [])
  }

  // Per-unit override state and helpers
  const [openUnits, setOpenUnits] = useState<Set<number>>(new Set())

  const toggleUnit = (index: number) => {
    const newOpen = new Set(openUnits)
    if (newOpen.has(index)) newOpen.delete(index)
    else newOpen.add(index)
    setOpenUnits(newOpen)
  }

  const getOverride = (index: number) => {
    return watchedValues.unit_overrides?.find(o => o.index === index)
  }

  const updateUnitDoors = (index: number, doors: UnitDoorsConfig) => {
    const currentOverrides = watchedValues.unit_overrides || []
    const existingIndex = currentOverrides.findIndex(o => o.index === index)
    if (existingIndex >= 0) {
      const updated = [...currentOverrides]
      updated[existingIndex] = { ...updated[existingIndex], doors }
      setValue('unit_overrides', updated)
    } else {
      setValue('unit_overrides', [...currentOverrides, { index, doors }])
    }
  }

  const removeOverride = (index: number) => {
    const currentOverrides = watchedValues.unit_overrides || []
    setValue('unit_overrides', currentOverrides.filter(o => o.index !== index))
  }

  const getEffectiveUnitDoors = (index: number): UnitDoorsConfig => {
    const override = getOverride(index)
    if (override?.doors) return override.doors
    const defaultDoor = createDefaultTHangarDoorSpec(watchedValues)
    return { front: [defaultDoor] }
  }

  const copyDoorsToAllUnits = (sourceIndex: number) => {
    const sourceDoors = getEffectiveUnitDoors(sourceIndex)
    const currentOverrides = watchedValues.unit_overrides || []
    const newOverrides = Array.from({ length: watchedValues.unit_count }, (_, i) => ({
      index: i,
      type: currentOverrides.find(o => o.index === i)?.type,
      doors: sourceDoors,
    }))
    setValue('unit_overrides', newOverrides)
  }

  // Show position
  const latitude = previewLatitude?.toFixed(6) || watchedValues.latitude?.toFixed(6) || '—'
  const longitude = previewLongitude?.toFixed(6) || watchedValues.longitude?.toFixed(6) || '—'

  const onSubmit = async (values: THangarParams) => {
    const pricingValid = await pricingForm.trigger()
    if (!pricingValid) return

    if (previewLatitude !== undefined && previewLongitude !== undefined) {
      values.latitude = previewLatitude
      values.longitude = previewLongitude
    }
    onSave(values, pricingForm.getValues())
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col h-full flex-1 min-h-0">
      <ScrollArea className="flex-1 min-h-0">
        <div className="p-4 space-y-6">
          {/* Building Section Header */}
          <div className="bg-blue-50 dark:bg-blue-950/30 -mx-4 px-4 py-2 border-y">
            <h3 className="font-semibold text-sm text-blue-700 dark:text-blue-300">T-Hangar Row</h3>
            <p className="text-xs text-muted-foreground">Alternating T-shaped units in a single row</p>
          </div>

          {/* Identity Section */}
          <div className="space-y-3">
            <h4 className="font-medium text-sm text-muted-foreground">Identity</h4>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-2">
                <Label htmlFor="name">Name</Label>
                <Input id="name" {...register('name')} placeholder="e.g., T-Hangar Row A" />
                {errors.name && <p className="text-xs text-destructive">{errors.name.message}</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="code">Code</Label>
                <Input id="code" {...register('code')} placeholder="e.g., TH-A" />
                {errors.code && <p className="text-xs text-destructive">{errors.code.message}</p>}
              </div>
            </div>
          </div>

          <Separator />

          {/* T-Shape Dimensions */}
          <div className="space-y-3">
            <h4 className="font-medium text-sm text-muted-foreground">T-Shape Dimensions (feet)</h4>

            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-2">
                <Label htmlFor="unit_width_ft">Unit Width (wing span)</Label>
                <Input
                  id="unit_width_ft"
                  type="number"
                  step="0.5"
                  {...register('unit_width_ft', { valueAsNumber: true })}
                />
                {errors.unit_width_ft && (
                  <p className="text-xs text-destructive">{errors.unit_width_ft.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="tail_width_ft">Tail Width</Label>
                <Input
                  id="tail_width_ft"
                  type="number"
                  step="0.5"
                  {...register('tail_width_ft', { valueAsNumber: true })}
                />
                {errors.tail_width_ft && (
                  <p className="text-xs text-destructive">{errors.tail_width_ft.message}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-2">
                <Label htmlFor="wing_depth_ft">Wing Depth</Label>
                <Input
                  id="wing_depth_ft"
                  type="number"
                  step="0.5"
                  {...register('wing_depth_ft', { valueAsNumber: true })}
                />
                {errors.wing_depth_ft && (
                  <p className="text-xs text-destructive">{errors.wing_depth_ft.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="height_ft">Height</Label>
                <Input
                  id="height_ft"
                  type="number"
                  step="0.5"
                  {...register('height_ft', { valueAsNumber: true })}
                />
                {errors.height_ft && (
                  <p className="text-xs text-destructive">{errors.height_ft.message}</p>
                )}
              </div>
            </div>

            {/* Wall Thickness */}
            <div className="space-y-2">
              <Label htmlFor="wall_thickness_ft">Wall Thickness (ft)</Label>
              <Input
                id="wall_thickness_ft"
                type="number"
                step="0.25"
                min={0.25}
                max={2}
                {...register('wall_thickness_ft', { valueAsNumber: true })}
              />
              {errors.wall_thickness_ft && (
                <p className="text-xs text-destructive">{errors.wall_thickness_ft.message}</p>
              )}
            </div>
          </div>

          <Separator />

          {/* Derived Values (read-only) */}
          <div className="space-y-2">
            <h4 className="font-medium text-sm text-muted-foreground">Calculated Values</h4>
            <div className="bg-muted/50 rounded-md p-3 text-xs space-y-1.5">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Step width:</span>
                <span className="font-medium">{stepWidth.toFixed(1)} ft</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Unit pitch (c-to-c):</span>
                <span className="font-medium">{unitPitch.toFixed(1)} ft</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">T-shape area:</span>
                <span className="font-medium">{unitArea.toFixed(0)} sq ft</span>
              </div>
              <Separator className="my-1" />
              <div className="flex justify-between">
                <span className="text-muted-foreground">Building depth:</span>
                <span className="font-medium">{layoutResult.buildingDepth.toFixed(1)} ft</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Row length:</span>
                <span className="font-medium">{layoutResult.rowLength.toFixed(1)} ft</span>
              </div>
            </div>
          </div>

          <Separator />

          {/* Unit Count */}
          <div className="bg-green-50 dark:bg-green-950/30 -mx-4 px-4 py-2 border-y">
            <h3 className="font-semibold text-sm text-green-700 dark:text-green-300">Unit Configuration</h3>
          </div>

          <div className="space-y-4">
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <Label>Number of Units</Label>
                <span className="text-sm font-medium">{watchedValues.unit_count}</span>
              </div>
              <Slider
                value={[watchedValues.unit_count]}
                onValueChange={handleUnitCountChange}
                min={2}
                max={30}
                step={1}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>2</span>
                <span>30</span>
              </div>
            </div>

            {/* Simplified T-Hangar Layout Preview */}
            <div className="space-y-2">
              <Label>Row Layout</Label>
              <THangarLayoutPreview
                unitCount={watchedValues.unit_count}
                unitWidth={watchedValues.unit_width_ft}
                tailWidth={watchedValues.tail_width_ft}
              />
            </div>

            {/* Warnings */}
            {layoutResult.warnings.length > 0 && (
              <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-md p-3">
                {layoutResult.warnings.map((warning, i) => (
                  <p key={i} className="text-xs text-amber-700 dark:text-amber-300">{warning}</p>
                ))}
              </div>
            )}
          </div>

          <Separator />

          {/* Door Configuration */}
          <div className="space-y-3">
            <h4 className="font-medium text-sm text-muted-foreground">Door Configuration</h4>
            <div className="space-y-2">
              <Label htmlFor="default_door_type">Door Type</Label>
              <Select
                value={watchedValues.default_door_type}
                onValueChange={(value) => setValue('default_door_type', value as DoorType)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="rolling">Rolling (20-60 ft)</SelectItem>
                  <SelectItem value="bifold">Bi-fold (30-100 ft)</SelectItem>
                  <SelectItem value="sliding">Sliding (40-200 ft)</SelectItem>
                  <SelectItem value="hydraulic">Hydraulic (50-150 ft)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-2">
                <Label htmlFor="default_door_width_ft">Door Width (ft)</Label>
                <Input
                  id="default_door_width_ft"
                  type="number"
                  step="0.5"
                  {...register('default_door_width_ft', { valueAsNumber: true })}
                />
                {errors.default_door_width_ft && (
                  <p className="text-xs text-destructive">{errors.default_door_width_ft.message}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="default_door_height_ft">Door Height (ft)</Label>
                <Input
                  id="default_door_height_ft"
                  type="number"
                  step="0.5"
                  {...register('default_door_height_ft', { valueAsNumber: true })}
                />
                {errors.default_door_height_ft && (
                  <p className="text-xs text-destructive">{errors.default_door_height_ft.message}</p>
                )}
              </div>
            </div>

            {/* Effective door width readout */}
            <div className="bg-muted/50 rounded-md px-3 py-2 text-xs">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Effective door width:</span>
                <span className="font-medium">
                  {Math.min(watchedValues.default_door_width_ft || 0, (watchedValues.unit_width_ft || 0) - 4).toFixed(1)} ft
                </span>
              </div>
              <p className="text-muted-foreground mt-1">
                Clamped to unit width minus 4 ft for frame clearance
              </p>
            </div>
          </div>

          <Separator />

          {/* Individual Unit Adjustments */}
          <div className="space-y-3">
            <h4 className="font-medium text-sm text-muted-foreground">Individual Unit Adjustments</h4>
            <p className="text-xs text-muted-foreground">Override door for specific units</p>

            <div className="space-y-2">
              {Array.from({ length: watchedValues.unit_count }, (_, i) => {
                const override = getOverride(i)
                const isOpen = openUnits.has(i)
                const hasDoorOverride = override?.doors !== undefined
                const effectiveDoors = getEffectiveUnitDoors(i)
                const frontDoor = effectiveDoors.front?.[0]

                return (
                  <Collapsible key={i} open={isOpen} onOpenChange={() => toggleUnit(i)}>
                    <CollapsibleTrigger asChild>
                      <button
                        type="button"
                        className={cn(
                          'w-full flex items-center justify-between p-2 rounded-md text-sm',
                          'hover:bg-muted/50 transition-colors',
                          hasDoorOverride && 'bg-amber-50 dark:bg-amber-950/30'
                        )}
                      >
                        <div className="flex items-center gap-2">
                          {isOpen ? (
                            <ChevronDown className="h-4 w-4" />
                          ) : (
                            <ChevronRight className="h-4 w-4" />
                          )}
                          <span>Unit {i + 1}</span>
                          <span className="text-xs text-muted-foreground">
                            ({i % 2 === 0 ? 'S' : 'N'})
                          </span>
                          {hasDoorOverride && (
                            <span className="text-xs text-amber-600 dark:text-amber-400">(custom)</span>
                          )}
                        </div>
                        <span className="text-muted-foreground text-xs">
                          {frontDoor ? `${frontDoor.width_ft.toFixed(0)}×${frontDoor.height_ft.toFixed(0)} ft` : 'No door'}
                        </span>
                      </button>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <div className="p-3 border rounded-md mt-1 space-y-4">
                        <THangarUnitDoorEditor
                          unitIndex={i}
                          unitCount={watchedValues.unit_count}
                          doors={getEffectiveUnitDoors(i)}
                          onChange={(doors) => updateUnitDoors(i, doors)}
                          unitWidthFt={watchedValues.unit_width_ft}
                          unitHeightFt={watchedValues.height_ft}
                          onCopyToAll={() => copyDoorsToAllUnits(i)}
                        />
                        {hasDoorOverride && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="text-xs text-muted-foreground"
                            onClick={() => removeOverride(i)}
                          >
                            Reset to default
                          </Button>
                        )}
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                )
              })}
            </div>
          </div>

          <Separator />

          {/* Appearance */}
          <div className="space-y-3">
            <h4 className="font-medium text-sm text-muted-foreground">Appearance</h4>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="floor_color">Floor Color</Label>
                <div className="flex gap-2">
                  <Input
                    id="floor_color"
                    type="color"
                    className="w-10 h-9 p-1 cursor-pointer"
                    {...register('floor_color')}
                  />
                  <Input
                    type="text"
                    className="flex-1"
                    {...register('floor_color')}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="wall_color">Wall Color</Label>
                <div className="flex gap-2">
                  <Input
                    id="wall_color"
                    type="color"
                    className="w-10 h-9 p-1 cursor-pointer"
                    {...register('wall_color')}
                  />
                  <Input
                    type="text"
                    className="flex-1"
                    {...register('wall_color')}
                  />
                </div>
              </div>
            </div>
          </div>

          <Separator />

          {/* Position */}
          <div className="space-y-3">
            <h4 className="font-medium text-sm text-muted-foreground">Position</h4>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label className="text-xs">Latitude</Label>
                <p className="text-xs font-mono bg-muted/50 px-2 py-1.5 rounded">{latitude}</p>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Longitude</Label>
                <p className="text-xs font-mono bg-muted/50 px-2 py-1.5 rounded">{longitude}</p>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="bearing_deg">Bearing (degrees)</Label>
              <Input
                id="bearing_deg"
                type="number"
                step="any"
                {...register('bearing_deg', { valueAsNumber: true })}
              />
            </div>
          </div>

          <Separator />

          {/* Pricing & Features */}
          <PricingFeaturesSection form={pricingForm} />

          {/* Delete button (edit mode only) */}
          {areaId && (
            <>
              <Separator />
              <RemoveAreaButton
                areaId={areaId}
                areaName={areaName}
                onRemoved={onRemoved}
              />
            </>
          )}
        </div>
      </ScrollArea>

      {/* Action Buttons */}
      <div className="border-t px-4 py-3 flex gap-2 shrink-0">
        <Button
          type="button"
          variant="outline"
          className="flex-1"
          onClick={onCancel}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          className="flex-1"
          disabled={isSaving || (!isPlaced && isCreating)}
        >
          {isSaving ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : isCreating ? (
            'Create T-Hangar'
          ) : (
            'Save Changes'
          )}
        </Button>
      </div>
    </form>
  )
}

// ============================================================================
// Inline T-Hangar Layout Preview SVG
// ============================================================================

function THangarLayoutPreview({
  unitCount,
  unitWidth,
  tailWidth,
}: {
  unitCount: number
  unitWidth: number
  tailWidth: number
}) {
  const stepWidth = (unitWidth - tailWidth) / 2
  const pitch = (unitWidth + tailWidth) / 2

  // SVG dimensions
  const svgWidth = 380
  const svgHeight = 60
  const padding = 4

  // Scale to fit
  const totalRowWidth = (unitCount - 1) * pitch + unitWidth
  const scale = (svgWidth - 2 * padding) / totalRowWidth

  // Half building depth for visualization
  const halfDepth = svgHeight / 2

  return (
    <div className="border rounded-md bg-muted/30 p-1">
      <svg width={svgWidth} height={svgHeight} className="w-full">
        {Array.from({ length: unitCount }, (_, i) => {
          const x = padding + i * pitch * scale
          const southFacing = i % 2 === 0
          const w = unitWidth * scale
          const tw = tailWidth * scale
          const sw = stepWidth * scale

          // Colors
          const fill = southFacing ? '#93c5fd' : '#fca5a5' // blue-300 / red-300
          const stroke = southFacing ? '#3b82f6' : '#ef4444' // blue-500 / red-500

          if (southFacing) {
            // Wing at top, tail at bottom
            return (
              <g key={i}>
                {/* Wing bay (top half) */}
                <rect x={x} y={0} width={w} height={halfDepth} fill={fill} stroke={stroke} strokeWidth={0.5} />
                {/* Tail bay (bottom half, narrower) */}
                <rect x={x + sw} y={halfDepth} width={tw} height={halfDepth} fill={fill} stroke={stroke} strokeWidth={0.5} />
                {/* Unit number */}
                <text x={x + w / 2} y={halfDepth - 4} textAnchor="middle" fontSize={8} fill={stroke} fontWeight="bold">
                  {i + 1}
                </text>
              </g>
            )
          } else {
            // Wing at bottom, tail at top
            return (
              <g key={i}>
                {/* Tail bay (top half, narrower) */}
                <rect x={x + sw} y={0} width={tw} height={halfDepth} fill={fill} stroke={stroke} strokeWidth={0.5} />
                {/* Wing bay (bottom half) */}
                <rect x={x} y={halfDepth} width={w} height={halfDepth} fill={fill} stroke={stroke} strokeWidth={0.5} />
                {/* Unit number */}
                <text x={x + w / 2} y={halfDepth + 12} textAnchor="middle" fontSize={8} fill={stroke} fontWeight="bold">
                  {i + 1}
                </text>
              </g>
            )
          }
        })}
      </svg>
    </div>
  )
}
